import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useLocation, useHistory } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import DatePicker from 'react-datepicker';
import { format, subDays, startOfYear } from 'date-fns';
import { theme } from 'styles/theme';
import InvoiceDetailCard from './InvoiceDetailCard';
import DownloadReconciliationReport from './components/DownloadReconciliationReport';
import ConnectionStatusCard from './components/ConnectionStatusCard';
import AppliedPaymentsReconciliationCard from './components/AppliedPaymentsReconciliationCard';
import FileManagement from './components/FileManagement';
import 'react-datepicker/dist/react-datepicker.css';

// Styled Components
const PageWrapper = styled.div`
  background: white;
  padding: 12px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  padding: 16px 20px 16px 20px;
  
  .header-content {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  
  .header-text h1 {
    color: ${theme.color.main};
    margin: 0 0 4px 0;
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }
  
  .header-text p {
    color: ${theme.color.subtext};
    font-size: 14px;
    margin: 0;
    line-height: 1.4;
  }
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 20px 20px 20px;
  background: white;
  gap: 20px;
  flex-wrap: wrap;
  border-bottom: 1px solid #f3f4f6;
  
  @media (max-width: 1024px) {
    gap: 16px;
    padding: 12px 16px 16px 16px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px 12px 16px 12px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding: 8px 8px 12px 8px;
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  width: 100%;
  
  .date-picker-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
      font-weight: 500;
      color: ${theme.color.text};
      font-size: 12px;
      white-space: nowrap;
    }
    
    .date-inputs {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }
    
    .date-input-wrapper {
      position: relative;
      
      .date-picker-input {
        padding: 6px 10px;
        border: 1px solid #d1d5db;
        border-radius: 4px;
        font-size: 12px;
        width: 120px;
        min-width: 120px;
        height: 32px;
        min-height: 32px;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
        text-align: center;
        box-sizing: border-box;
        
        &:hover {
          border-color: #9ca3af;
        }
        
        &:focus {
          outline: none;
          border-color: ${theme.color.main};
          box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.1);
        }
        
        &::placeholder {
          color: #9ca3af;
        }

        &.error {
          border-color: #ef4444;
          box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
        }
      }
    }
    
    .date-separator {
      color: ${theme.color.subtext};
      font-size: 12px;
      font-weight: 500;
    }

    .date-validation-error {
      color: #ef4444;
      font-size: 11px;
      margin-top: 4px;
      font-weight: 500;
    }

    .date-validation-warning {
      color: #f59e0b;
      font-size: 11px;
      margin-top: 4px;
      font-weight: 500;
    }

    .date-range-info {
      margin-top: 4px;
      font-size: 11px;
      color: ${theme.color.subtext};
      
      .range-days {
        font-weight: 500;
        color: ${theme.color.main};
      }
    }
  }
  
  .quick-date-buttons {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  .report-button-container {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
  
  .quick-date-btn {
    padding: 6px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    color: ${theme.color.text};
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 32px;
    min-height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    box-sizing: border-box;
    
    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
    
    &:active {
      background: #f3f4f6;
    }
    
    &:focus {
      outline: none;
      border-color: ${theme.color.main};
      box-shadow: 0 0 0 2px rgba(255, 165, 0, 0.1);
    }
    
    &.active {
      background: ${theme.color.main};
      color: white;
      border-color: ${theme.color.main};
    }
  }
  
  @media (max-width: 1024px) {
    gap: 12px;
    
    .date-picker-group {
      .date-inputs {
        gap: 6px;
      }
      
      .date-input-wrapper .date-picker-input {
        width: 110px;
        min-width: 110px;
        height: 30px;
        min-height: 30px;
      }
    }
    
    .quick-date-buttons {
      gap: 6px;
      
      .quick-date-btn {
        padding: 5px 8px;
        font-size: 10px;
        height: 30px;
        min-height: 30px;
      }
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    
    .date-picker-group {
      .date-inputs {
        justify-content: center;
        gap: 6px;
      }
      
      .date-input-wrapper .date-picker-input {
        width: 100px;
        min-width: 100px;
        height: 28px;
        min-height: 28px;
      }
    }
    
    .quick-date-buttons {
      justify-content: center;
      gap: 6px;
      
      .quick-date-btn {
        padding: 4px 6px;
        font-size: 9px;
        height: 28px;
        min-height: 28px;
      }
    }
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    
    .date-picker-group {
      .date-inputs {
        flex-direction: column;
        align-items: center;
        gap: 4px;
      }
      
      .date-separator {
        display: none;
      }
      
      .date-input-wrapper .date-picker-input {
        width: 120px;
        min-width: 120px;
        height: 26px;
        min-height: 26px;
      }
    }
    
    .quick-date-buttons {
      justify-content: center;
      gap: 4px;
      
      .quick-date-btn {
        padding: 3px 6px;
        font-size: 8px;
        height: 26px;
        min-height: 26px;
        flex: 1;
        max-width: 70px;
      }
    }
  }
`;

const MetadataCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 0 20px 20px 20px;
`;

const MetadataHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const MetadataTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.color.text};
`;

const MetadataExpandIcon = styled.span<{ isExpanded: boolean }>`
  font-size: 10px;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.isExpanded ? '180deg' : '0deg'});
  color: #6b7280;
`;

const MetadataContent = styled.div`
  padding: 16px;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  .label {
    font-size: 11px;
    color: ${theme.color.subtext};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
  
  .value {
    font-size: 13px;
    font-weight: 600;
    color: ${theme.color.text};
    word-break: break-word;
  }
`;

const ErrorMessage = styled.div`
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #dc2626;
  padding: 20px 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid #fca5a5;
  box-shadow: 0 4px 6px rgba(220, 38, 38, 0.1);
  text-align: center;
  line-height: 1.5;
`;

const LoadingMessage = styled.div`
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1d4ed8;
  padding: 20px 24px;
  border-radius: 12px;
  margin-bottom: 24px;
  font-size: 14px;
  font-weight: 500;
  border: 1px solid #93c5fd;
  box-shadow: 0 4px 6px rgba(29, 78, 216, 0.1);
  text-align: center;
  line-height: 1.5;
`;

interface LocalSummaryData {
  metadata: {
    companyId: number;
    startDate: string;
    endDate: string;
    generatedAt: string;
    description: string;
  };
  invoices: {
    total_invoices: number;
    synced_to_qbo: number;
    not_synced_to_qbo: number;
    sync_successful: number;
    sync_failed: number;
    has_errors: number;
    archived: number;
    not_sent: number;
    total_amount_due: number;
    avg_amount_due: number;
    sync_percentage: number;
  };
  invoiceStatusBreakdown: Array<{
    status_label: string;
    status_color: string;
    count: number;
  }>;
}

const QuickBooksReconciliation: React.FC = () => {
  console.log('QuickBooksReconciliation component rendering');
  
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') 
      ? new Date(searchParams.get('startDate')!) 
      : startOfYear(new Date()),
    endDate: searchParams.get('endDate') 
      ? new Date(searchParams.get('endDate')!) 
      : new Date()
  });
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);

  // Update URL when date range changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('startDate', format(dateRange.startDate, 'yyyy-MM-dd'));
    params.set('endDate', format(dateRange.endDate, 'yyyy-MM-dd'));
    history.replace(`${location.pathname}?${params.toString()}`);
  }, [dateRange, location.pathname, history, location.search]);

  // Fetch local summary data
  const { data: localSummary, isLoading, error } = useQuery(
    ["local-summary", dateRange],
    async () => {
      try {
        const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:4000';
        console.log('Fetching reconciliation data from:', serverUrl);
        
        const response = await axios.get<{ success: boolean; data: LocalSummaryData }>(
          `${serverUrl}/api/v1/company/quickbooks-online/reconciliation/local-summary`,
          {
            params: {
              startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
              endDate: format(dateRange.endDate, 'yyyy-MM-dd')
            }
          }
        );
        
        console.log('Reconciliation data received:', response.data);
        return response.data.data;
      } catch (error) {
        console.error('Error fetching reconciliation data:', error);
        throw error;
      }
    },
    {
      enabled: true,
      retry: 1,
      onError: (error) => {
        console.error('Query error:', error);
      }
    }
  );

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      setDateRange(prev => ({ ...prev, startDate: date }));
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      setDateRange(prev => ({ ...prev, endDate: date }));
    }
  };

  const handleQuickDateSelect = (days: number) => {
    const endDate = new Date();
    const startDate = subDays(endDate, days);
    setDateRange({ startDate, endDate });
  };

  const handleYTDSelect = () => {
    const endDate = new Date();
    const startDate = startOfYear(new Date());
    setDateRange({ startDate, endDate });
  };

  // Validate date range
  const isDateRangeValid = dateRange.startDate <= dateRange.endDate;
  const isEndDateInFuture = dateRange.endDate > new Date();

  // Get date range in days for display
  const getDateRangeDays = () => {
    const diffTime = Math.abs(dateRange.endDate.getTime() - dateRange.startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Check if current selection is YTD
  const isYTDSelected = () => {
    const currentYear = new Date().getFullYear();
    const startOfYear = new Date(currentYear, 0, 1); // January 1st of current year
    const today = new Date();
    
    return dateRange.startDate.getTime() === startOfYear.getTime() && 
           dateRange.endDate.getDate() === today.getDate() &&
           dateRange.endDate.getMonth() === today.getMonth() &&
           dateRange.endDate.getFullYear() === today.getFullYear();
  };

  const formatDateDisplay = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  console.log('Rendering QuickBooks reconciliation dashboard');
  
  return (
    <PageWrapper>
      <Header>
        <div className="header-content">
          <div className="header-text">
            <h1>QuickBooks Online Dashboard</h1>
            <p>Comprehensive QBO integration management and reconciliation</p>
          </div>
        </div>
      </Header>

        {/* Experimental Notice */}
        <div className="mb-6 mx-5 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-yellow-600 text-lg">🧪</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Experimental Feature
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  This reconciliation dashboard is currently under development and may contain experimental features. 
                  For production use, please continue using the standard QuickBooks Online tab above.
                </p>
              </div>
            </div>
          </div>
        </div>

        <ControlRow>
          <DateRangeContainer>
            <div className="date-picker-group">
              <label htmlFor="start-date-picker">Date Range:</label>
              <div className="date-inputs">
                <div className="date-input-wrapper">
                  <DatePicker
                    id="start-date-picker"
                    selected={dateRange.startDate}
                    onChange={handleStartDateChange}
                    selectsStart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    maxDate={dateRange.endDate}
                    dateFormat="MMM dd, yyyy"
                    className={`date-picker-input ${!isDateRangeValid ? 'error' : ''}`}
                    placeholderText="Start Date"
                    popperPlacement="bottom-start"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    title="Select start date for the reconciliation report"
                  />
                </div>
                <span className="date-separator">to</span>
                <div className="date-input-wrapper">
                  <DatePicker
                    id="end-date-picker"
                    selected={dateRange.endDate}
                    onChange={handleEndDateChange}
                    selectsEnd
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    minDate={dateRange.startDate}
                    maxDate={new Date()}
                    dateFormat="MMM dd, yyyy"
                    className={`date-picker-input ${!isDateRangeValid || isEndDateInFuture ? 'error' : ''}`}
                    placeholderText="End Date"
                    popperPlacement="bottom-end"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={10}
                    title="Select end date for the reconciliation report"
                  />
                </div>
              </div>
              {!isDateRangeValid && (
                <div className="date-validation-error">
                  Start date must be before or equal to end date
                </div>
              )}
              {isEndDateInFuture && (
                <div className="date-validation-warning">
                  End date cannot be in the future
                </div>
              )}
              <div className="date-range-info">
                {isDateRangeValid && (
                  <span className="range-days">
                    {getDateRangeDays()} day{getDateRangeDays() !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <div className="quick-date-buttons">
              <button 
                onClick={() => handleQuickDateSelect(7)}
                className={`quick-date-btn ${getDateRangeDays() === 7 ? 'active' : ''}`}
                title="Select last 7 days"
                aria-label="Select last 7 days"
              >
                Last 7 days
              </button>
              <button 
                onClick={() => handleQuickDateSelect(30)}
                className={`quick-date-btn ${getDateRangeDays() === 30 ? 'active' : ''}`}
                title="Select last 30 days"
                aria-label="Select last 30 days"
              >
                Last 30 days
              </button>
              <button 
                onClick={() => handleQuickDateSelect(90)}
                className={`quick-date-btn ${getDateRangeDays() === 90 ? 'active' : ''}`}
                title="Select last 90 days"
                aria-label="Select last 90 days"
              >
                Last 90 days
              </button>
              <button 
                onClick={handleYTDSelect}
                className={`quick-date-btn ${isYTDSelected() ? 'active' : ''}`}
                title="Select Year to Date"
                aria-label="Select Year to Date"
              >
                YTD
              </button>
            </div>
            <div className="report-button-container">
              <DownloadReconciliationReport
                dateRange={{
                  startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
                  endDate: format(dateRange.endDate, 'yyyy-MM-dd')
                }}
                format="csv"
                disabled={isLoading || !localSummary || !isDateRangeValid || isEndDateInFuture}
                onStart={() => {}}
                onComplete={() => {}}
                onError={(error) => console.error('Download error:', error)}
              />
            </div>
            
            {/* Enhanced Report Notice */}
            <div className="mt-2 text-xs text-gray-600 text-center">
              📊 Enhanced report with 40+ columns including service locations, billing details, and sync status
            </div>
          </DateRangeContainer>
        </ControlRow>

        <MetadataCard>
          <MetadataHeader onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}>
            <MetadataTitle>Report Summary</MetadataTitle>
            <MetadataExpandIcon isExpanded={isMetadataExpanded}>▼</MetadataExpandIcon>
          </MetadataHeader>
          
          {isMetadataExpanded && (
            <MetadataContent>
              <MetadataGrid>
                <MetadataItem>
                  <div className="label">Company ID</div>
                  <div className="value">{localSummary?.metadata.companyId || '-'}</div>
                </MetadataItem>
                <MetadataItem>
                  <div className="label">Generated</div>
                  <div className="value">{localSummary ? new Date(localSummary.metadata.generatedAt).toLocaleDateString() : '-'}</div>
                </MetadataItem>
                <MetadataItem>
                  <div className="label">Date Range</div>
                  <div className="value">{formatDateDisplay(format(dateRange.startDate, 'yyyy-MM-dd'))} - {formatDateDisplay(format(dateRange.endDate, 'yyyy-MM-dd'))}</div>
                </MetadataItem>
                <MetadataItem>
                  <div className="label">Description</div>
                  <div className="value">{localSummary?.metadata.description || 'Local data summary for reconciliation'}</div>
                </MetadataItem>
              </MetadataGrid>
            </MetadataContent>
          )}
        </MetadataCard>

        {error && (
          <div style={{ margin: '0 20px 24px 20px' }}>
            <ErrorMessage>
              Error loading local summary: {(error as Error).message}
            </ErrorMessage>
          </div>
        )}

        {isLoading && (
          <div style={{ margin: '0 20px 24px 20px' }}>
            <LoadingMessage>Loading local data summary...</LoadingMessage>
          </div>
        )}

        <div style={{ margin: '0 20px 24px 20px' }}>
          <ConnectionStatusCard />
        </div>
        
        {localSummary && (
          <div style={{ margin: '0 20px 24px 20px' }}>
            <InvoiceDetailCard 
              data={{
                total: localSummary.invoices.total_invoices,
                synced: localSummary.invoices.synced_to_qbo,
                notSynced: localSummary.invoices.not_synced_to_qbo,
                syncFailed: localSummary.invoices.sync_failed,
                hasErrors: localSummary.invoices.has_errors,
                archived: localSummary.invoices.archived,
                statusBreakdown: localSummary.invoiceStatusBreakdown
              }}
              dateRange={{
                startDate: format(dateRange.startDate, 'yyyy-MM-dd'),
                endDate: format(dateRange.endDate, 'yyyy-MM-dd')
              }}
            />
          </div>
        )}

        {/* Placeholder: Applied Payments Reconciliation */}
        <div style={{ margin: '0 20px 24px 20px' }}>
          <AppliedPaymentsReconciliationCard />
        </div>

        {/* File Management */}
        <div style={{ margin: '0 20px 24px 20px' }}>
          <FileManagement />
        </div>
    </PageWrapper>
  );
};





export default QuickBooksReconciliation; 
